import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { updateProfileDetails } from '../../features/auth/authSlice';
import { clearFloorPlan } from '../../features/floorplan/floorplanSlice';
import { clearLayout } from '../../features/layout/layoutSlice';
import { clearFacility } from '../../features/facility/facilitySlice';
import { clearEquipment } from '../../features/equipment/equipmentSlice';
import { clearDxfImport } from '../../features/dxfImport/dxfImportSlice';
import { fetchUser, updateUser } from '../../utils/usersApi';
import { endAuthSession } from '../../utils/sessionLogout';
import { PoweredByPathfinder } from '../common/PoweredByPathfinder';

const inputClassName =
  'mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500';

const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authUsername = useAppSelector((state) => state.auth.username);
  const userId = useAppSelector((state) => state.auth.userId);
  const authEmail = useAppSelector((state) => state.auth.email);
  const authFullName = useAppSelector((state) => state.auth.fullName);

  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const [username, setUsername] = useState(authUsername ?? '');
  const [fullName, setFullName] = useState(authFullName ?? '');
  const [email, setEmail] = useState(authEmail ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const initials = useMemo(() => {
    const source = (fullName || username || 'U').trim();
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
  }, [fullName, username]);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [profileMenuOpen]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!userId) {
        setLoading(false);
        setProfileError('Signed-in user id is missing. Please log in again.');
        return;
      }

      setLoading(true);
      setProfileError(null);
      try {
        const user = await fetchUser(userId);
        if (cancelled) return;
        setUsername(user.username);
        setFullName(user.fullName ?? '');
        setEmail(user.email ?? '');
        dispatch(
          updateProfileDetails({
            username: user.username,
            email: user.email,
            fullName: user.fullName,
          })
        );
      } catch (err) {
        if (cancelled) return;
        setProfileError(err instanceof Error ? err.message : 'Failed to load profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, userId]);

  const handleLogout = async () => {
    dispatch(clearFacility());
    dispatch(clearEquipment());
    dispatch(clearDxfImport());
    dispatch(clearFloorPlan());
    dispatch(clearLayout());
    try {
      localStorage.removeItem('layout_editor_equipment');
    } catch (e) {
      console.error('Failed to clear equipment local storage:', e);
    }
    navigate('/', { replace: true });
    await endAuthSession();
  };

  const handleSaveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingProfile || !userId) return;

    setSavingProfile(true);
    setProfileError(null);
    setProfileSuccess(null);
    try {
      const updated = await updateUser(userId, {
        email: email.trim() || null,
        fullName: fullName.trim() || null,
      });
      setUsername(updated.username);
      setFullName(updated.fullName ?? '');
      setEmail(updated.email ?? '');
      dispatch(
        updateProfileDetails({
          username: updated.username,
          email: updated.email,
          fullName: updated.fullName,
        })
      );
      setProfileSuccess('Profile details saved.');
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingPassword || !userId) return;

    if (!password) {
      setPasswordError('Enter a new password.');
      setPasswordSuccess(null);
      return;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      setPasswordSuccess(null);
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      setPasswordSuccess(null);
      return;
    }

    setSavingPassword(true);
    setPasswordError(null);
    setPasswordSuccess(null);
    try {
      await updateUser(userId, { password });
      setPassword('');
      setConfirmPassword('');
      setPasswordSuccess('Password updated.');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#eff4fa] text-slate-900">
      <header className="flex h-[42px] shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 rounded-md px-1 py-0.5 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
            B
          </div>
          <span className="text-[11px] font-semibold text-slate-900">Peakaso</span>
        </button>
        <div className="flex items-center gap-4 text-[11px] text-slate-500">
          <span>v2.4.1</span>
          <div ref={profileMenuRef} className="relative">
            <button
              type="button"
              title="Account"
              aria-label="Account"
              onClick={() => setProfileMenuOpen((open) => !open)}
              className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                profileMenuOpen ? 'bg-slate-100 text-slate-700' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <UserIcon />
            </button>
            {profileMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 min-w-[150px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => setProfileMenuOpen(false)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100"
                >
                  <UserIcon />
                  Profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    setLogoutConfirmOpen(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100"
                >
                  <LogoutIcon />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-10">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-[-0.03em] text-slate-950">Profile</h1>
              <p className="mt-1 text-sm text-slate-500">
                Manage your account security and profile details.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              Back to projects
            </button>
          </div>

          {loading ? (
            <div
              className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-sm text-slate-500 shadow-sm"
              role="status"
            >
              Loading profile…
            </div>
          ) : (
            <div className="grid overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[300px_minmax(0,1fr)]">
              {/* Left panel — account / security */}
              <aside className="border-b border-slate-200 bg-slate-50/70 p-5 lg:border-b-0 lg:border-r">
                <h2 className="text-sm font-bold text-slate-900">Account</h2>
                <p className="mt-1 text-[12px] leading-5 text-slate-500">
                  Identity and password for this Peakaso session.
                </p>

                <div className="mt-5 flex flex-col items-center text-center">
                  <div className="flex h-28 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 text-3xl font-bold text-white shadow-sm">
                    {initials}
                  </div>
                  <div className="mt-3 min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {fullName || username || 'User'}
                    </div>
                    <div className="mt-0.5 truncate text-[12px] text-slate-500">@{username || '—'}</div>
                  </div>
                </div>

                <form onSubmit={handleChangePassword} className="mt-8 space-y-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Change password
                  </div>
                  <label className="block text-left">
                    <span className="text-[12px] font-medium text-slate-600">New password</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      disabled={savingPassword}
                      className={inputClassName}
                    />
                  </label>
                  <label className="block text-left">
                    <span className="text-[12px] font-medium text-slate-600">Confirm password</span>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                      disabled={savingPassword}
                      className={inputClassName}
                    />
                  </label>

                  {passwordError && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                      {passwordError}
                    </div>
                  )}
                  {passwordSuccess && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800">
                      {passwordSuccess}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={savingPassword || !userId}
                    className="mt-1 w-full rounded-md bg-slate-900 px-3 py-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {savingPassword ? 'Updating…' : 'Change Password'}
                  </button>
                </form>
              </aside>

              {/* Right panel — profile details */}
              <section className="p-5 sm:p-7">
                <form onSubmit={handleSaveProfile} className="space-y-8">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">Profile Information</h2>
                    <p className="mt-1 text-[12px] text-slate-500">
                      Basic account details used across the workspace.
                    </p>

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <label className="block">
                        <span className="text-[12px] font-medium text-slate-600">Username</span>
                        <input
                          type="text"
                          value={username}
                          readOnly
                          disabled
                          className={inputClassName}
                        />
                        <span className="mt-1 block text-[11px] text-slate-400">
                          Username cannot be changed.
                        </span>
                      </label>

                      <label className="block">
                        <span className="text-[12px] font-medium text-slate-600">Full name</span>
                        <input
                          type="text"
                          value={fullName}
                          onChange={(event) => setFullName(event.target.value)}
                          placeholder="Enter your full name"
                          autoComplete="name"
                          disabled={savingProfile}
                          className={inputClassName}
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-sm font-bold text-slate-900">Contact Info</h2>
                    <p className="mt-1 text-[12px] text-slate-500">
                      How we can reach you about this account.
                    </p>

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <label className="block sm:col-span-2">
                        <span className="text-[12px] font-medium text-slate-600">Email</span>
                        <input
                          type="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          placeholder="Enter your email"
                          autoComplete="email"
                          disabled={savingProfile}
                          className={inputClassName}
                        />
                      </label>

                      <div className="sm:col-span-2">
                        <span className="text-[12px] font-medium text-slate-600">User ID</span>
                        <div className="mt-1.5 break-all rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-700">
                          {userId || '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {profileError && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      {profileError}
                    </div>
                  )}
                  {profileSuccess && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                      {profileSuccess}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-5">
                    <button
                      type="submit"
                      disabled={savingProfile || !userId}
                      className="rounded-md bg-blue-600 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-blue-300"
                    >
                      {savingProfile ? 'Saving…' : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="rounded-md border border-slate-300 px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </section>
            </div>
          )}
        </div>
      </main>

      <footer className="flex h-8 shrink-0 items-center border-t border-slate-200 bg-white px-5 text-[11px] text-slate-500">
        <PoweredByPathfinder className="text-slate-600" />
      </footer>

      {logoutConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm"
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <LogoutIcon />
              </div>
              <div>
                <div className="text-base font-semibold text-slate-900">Log out?</div>
                <div className="mt-1 text-sm leading-5 text-slate-500">
                  This will end your session and return you to the login screen.
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setLogoutConfirmOpen(false)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  handleLogout();
                }}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function UserIcon({ large = false }: { large?: boolean }) {
  const size = large ? 22 : 13;
  return (
    <svg width={size} height={size} viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M3.24023 3.72461C3.24023 2.01277 4.62797 0.625001 6.33984 0.625001C8.05168 0.625001 9.43941 2.01277 9.43941 3.72461C9.43941 5.43645 8.05168 6.82422 6.33984 6.82422C4.62797 6.82422 3.24023 5.43645 3.24023 3.72461Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.625 12.5391C0.625 9.38281 3.18363 6.82418 6.33988 6.82418C9.49613 6.82418 12.0548 9.38281 12.0548 12.5391"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeMiterlimit="10"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 5H6.5A2.5 2.5 0 0 0 4 7.5v9A2.5 2.5 0 0 0 6.5 19H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 8l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default ProfilePage;
